import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { can } from '../../utils/permissions';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import Button from '../../components/ui/Button';
import { resolvePhotoUrl } from '../../utils/photoUrl';

/*
 * Home photographs (/admin/home-slides, 2026-10-18): the bank photographs
 * Home's "Rotating gallery" and "Single image" show (routes/home_slides.py),
 * for those who choose them (home:curate: experts, collection managers,
 * system admins).
 *
 * A photograph is added from wherever it is open ("Feature on Home" in the
 * photo viewer). Here each one gets its captions, whether it names its
 * species, whether it shows at all, which one is featured (what "Single
 * image" shows anyone who did not pick one), its place in the order, and
 * removal. One that stopped qualifying -- its record deleted, its species now
 * rare -- stays listed here, saying why, and Home simply leaves it out.
 */
function errorText(e) {
  return e?.response?.data?.error || e?.message || String(e);
}

export default function HomeSlides() {
  const { t, i18n } = useTranslation('homeSlides');
  const { user } = useAuth();
  // home:read reads the curators' list; home:curate changes it.
  const allowed = can(user, 'home:read');
  const mayEdit = can(user, 'home:curate');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [removeError, setRemoveError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const { data: body } = await api.get('/home/slides/manage');
      setData(body);
    } catch (e) {
      setError(errorText(e));
    }
  }, []);
  useEffect(() => { if (allowed) load(); }, [load, allowed]);

  if (!allowed) {
    return <p style={{ color: 'var(--c-text-muted-fg)' }} data-testid="home-slides-restricted">{t('restricted')}</p>;
  }

  const slides = data?.slides || [];
  const replace = (slide) => setData((d) => ({ ...d, slides: d.slides.map((s) => (s.slide_id === slide.slide_id ? slide : s)) }));
  const change = async (slide, body) => {
    setBusy(true);
    try {
      const { data: answer } = await api.put(`/home/slides/${slide.slide_id}`, body);
      if (body.is_featured) await load();       // the previous featured one changed too
      else replace(answer.slide);
      return null;
    } catch (e) {
      return errorText(e);
    } finally {
      setBusy(false);
    }
  };
  const move = async (from, to) => {
    const ids = slides.map((s) => s.slide_id);
    [ids[from], ids[to]] = [ids[to], ids[from]];
    setBusy(true);
    try {
      const { data: answer } = await api.post('/home/slides/order', { slide_ids: ids });
      setData((d) => ({ ...d, slides: answer.slides }));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    setRemoveError('');
    try {
      await api.delete(`/home/slides/${removing.slide_id}`);
      setData((d) => ({ ...d, slides: d.slides.filter((s) => s.slide_id !== removing.slide_id) }));
      setRemoving(null);
    } catch (e) {
      setRemoveError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h2>{t('title')}</h2>
      <p style={{ color: 'var(--c-text-muted-fg)', marginTop: 0, maxWidth: 720 }}>{t('intro')}</p>
      {data && (
        <p style={{ fontSize: 13, color: 'var(--c-text-muted-fg)' }} data-testid="home-slides-count">
          {t('count', { count: slides.length, max: data.max_slides })}
        </p>
      )}
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }} data-testid="home-slides-error">{t('loadFailed')} {error}</p>}
      {!data && !error && <p role="status">{t('loading')}</p>}
      {data && !slides.length && <p data-testid="home-slides-empty">{t('empty')}</p>}

      <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
        {slides.map((slide, i) => (
          <SlideRow key={slide.slide_id} slide={slide} t={t} lang={i18n?.language} busy={busy || !mayEdit}
            readOnly={!mayEdit}
            first={i === 0} last={i === slides.length - 1}
            onChange={(body) => change(slide, body)}
            onUp={() => move(i, i - 1)} onDown={() => move(i, i + 1)}
            onRemove={() => { setRemoveError(''); setRemoving(slide); }} />
        ))}
      </ol>

      <ConfirmDeleteModal
        open={!!removing}
        title={t('removeTitle')}
        consequences={[t('removeConsequence')]}
        deleting={busy && !!removing}
        error={removeError}
        confirmLabel={t('remove')}
        confirmingLabel={t('removing')}
        onConfirm={remove}
        onCancel={() => { if (!busy) setRemoving(null); }}
      />
    </div>
  );
}

function SlideRow({ slide, t, lang, busy, first, last, onChange, onUp, onDown, onRemove, readOnly = false }) {
  const [captionEn, setCaptionEn] = useState(slide.caption_en || '');
  const [captionHe, setCaptionHe] = useState(slide.caption_he || '');
  const [note, setNote] = useState('');
  const edited = captionEn !== (slide.caption_en || '') || captionHe !== (slide.caption_he || '');
  const name = slide.taxon ? (lang === 'he' && slide.taxon.hebrew_name) || slide.taxon.name : null;
  const save = async () => {
    const failed = await onChange({ caption_en: captionEn || null, caption_he: captionHe || null });
    setNote(failed ? t('saveFailed', { error: failed }) : t('saved'));
  };
  const toggle = async (body) => {
    const failed = await onChange(body);
    if (failed) setNote(t('saveFailed', { error: failed }));
  };

  return (
    <li data-testid="home-slide-row" data-slide={slide.slide_id}
      style={{ display: 'grid', gridTemplateColumns: 'minmax(96px, 160px) 1fr', gap: 12, padding: 12,
        border: '1px solid var(--c-border)', borderRadius: 10, background: 'var(--c-surface)',
        opacity: slide.active && slide.eligible ? 1 : 0.75 }}>
      <img src={resolvePhotoUrl(slide.image_small_url)} alt={slide.caption_en || name || ''}
        style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', borderRadius: 6, background: 'var(--c-bg-muted)' }} />
      <div style={{ display: 'grid', gap: 8, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: 'var(--c-text-muted-fg)' }}>
          {name && <i>{name}</i>}
          {name && slide.record_code ? ' · ' : ''}
          {slide.record_code && t('record', { code: slide.record_code })}
          {slide.featured && <strong style={{ marginInlineStart: 8, color: 'var(--c-primary-fg)' }}>★ {t('featured')}</strong>}
        </div>
        {!slide.eligible && (
          <p role="note" data-testid="home-slide-reasons" style={{ margin: 0, fontSize: 13, color: 'var(--c-danger-fg)' }}>
            {t('notShowing')} {slide.reasons.map((r) => t(`reason.${r}`, { defaultValue: r })).join(', ')}
          </p>
        )}
        <div style={{ display: 'grid', gap: 6, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <label style={labelStyle}>
            {t('captionEn')}
            <input value={captionEn} maxLength={200} onChange={(e) => setCaptionEn(e.target.value)} style={inputStyle}
              data-testid="home-slide-caption-en" readOnly={readOnly} />
          </label>
          <label style={labelStyle}>
            {t('captionHe')}
            <input value={captionHe} maxLength={200} dir="rtl" onChange={(e) => setCaptionHe(e.target.value)}
              style={inputStyle} data-testid="home-slide-caption-he" readOnly={readOnly} />
          </label>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', fontSize: 13 }}>
          <label title={t('tip.showTaxon')}>
            <input type="checkbox" checked={slide.show_taxon} disabled={busy}
              onChange={(e) => toggle({ show_taxon: e.target.checked })} /> {t('showTaxon')}
          </label>
          <label title={t('tip.active')}>
            <input type="checkbox" checked={slide.active} disabled={busy} data-testid="home-slide-active"
              onChange={(e) => toggle({ active: e.target.checked })} /> {t('active')}
          </label>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <Button variant="primary" disabled={!edited || busy} onClick={save} title={t('tip.save')}
            data-testid="home-slide-save">{t('save')}</Button>
          {!slide.featured && (
            <Button disabled={busy || !slide.active} onClick={() => toggle({ is_featured: true })}
              title={t('tip.featured')} data-testid="home-slide-feature">{t('makeFeatured')}</Button>
          )}
          <Button variant="ghost" disabled={busy || first} onClick={onUp} title={t('tip.moveUp')}
            aria-label={t('moveUp')} data-testid="home-slide-up">↑</Button>
          <Button variant="ghost" disabled={busy || last} onClick={onDown} title={t('tip.moveDown')}
            aria-label={t('moveDown')} data-testid="home-slide-down">↓</Button>
          <Button variant="ghost" disabled={busy} onClick={onRemove} title={t('tip.remove')}
            style={{ marginInlineStart: 'auto', color: 'var(--c-danger-fg)' }} data-testid="home-slide-remove">
            {t('remove')}
          </Button>
          {note && <span role="status" style={{ fontSize: 12 }}>{note}</span>}
        </div>
      </div>
    </li>
  );
}

const labelStyle = { display: 'grid', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--c-text-body-fg)' };
const inputStyle = {
  font: 'inherit', fontWeight: 400, fontSize: 14, padding: '6px 8px', borderRadius: 6,
  border: '1px solid var(--c-border-strong-2)', background: 'var(--c-surface)',
};
